import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import { getMediaUrl } from '../../services/media';
import styles from './Persons.module.css';

const LIMIT = 24;

const Persons = () => {
  const { t } = useLanguage();
  const [persons, setPersons] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const loaderRef = useRef(null);

  const fetchPersons = useCallback((currentPage, append = false) => {
    if (currentPage === 1) setInitialLoading(true);
    else setLoading(true);

    api.get('/persons', { params: { page: currentPage } })
      .then((res) => {
        const { persons: data = [], total = 0, page: p = 1 } = res.data;
        setPersons((prev) => append ? [...prev, ...data] : data);
        setHasMore(p * LIMIT < total);
      })
      .catch(() => {
        if (!append) setPersons([]);
        setHasMore(false);
      })
      .finally(() => {
        setInitialLoading(false);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchPersons(1, false);
  }, [fetchPersons]);

  useEffect(() => {
    if (page === 1) return;
    fetchPersons(page, true);
  }, [page, fetchPersons]);

  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !initialLoading) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, initialLoading]);

  if (initialLoading) return <div className={styles.loading}>{t('common.loading')}</div>;

  return (
    <div className={styles.persons}>
      <div className={styles.container}>
        <h1 className={styles.title}>{t('persons.title')}</h1>

        {persons.length === 0 ? (
          <div className={styles.empty}>Персоны не найдены</div>
        ) : (
          <div className={styles.grid}>
            {persons.map((person) => (
              <Link to={`/persons/${person.id}`} key={person.id} className={styles.card}>
                <div className={styles.imageWrapper}>
                  <div className={styles.image}>
                    {person.imageUrl && <img src={getMediaUrl(person.imageUrl)} alt={person.name} />}
                  </div>
                </div>
                <h3 className={styles.name}>{person.name}</h3>
                <p className={styles.role}>{person.role}</p>
                <span className={styles.link}>{t('persons.viewDetails')}</span>
              </Link>
            ))}
          </div>
        )}

        <div ref={loaderRef} className={styles.loaderTrigger}>
          {loading && <div className={styles.loadingMore}>{t('common.loading')}</div>}
          {!hasMore && persons.length > 0 && (
            <p className={styles.endMessage}>Все персоны загружены</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Persons;
